import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import type { PdpProduct } from 'components/discovery/pdp/typing/pdp-product.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import { HeadingSlotMixin } from 'components/shared/heading/heading-slot-mixin.js'
import { style } from './product-description.style.js'

export const productDescriptionDataSources = ['description', 'description2', 'description3'] as const
export type ProductDescriptionDataSource = typeof productDescriptionDataSources[number]
export type ProductDescriptions = Pick<PdpProduct, ProductDescriptionDataSource>

export class CscProductDescriptionReadMoreEvent extends CustomEvent<{ target: string }> {
  static readonly type = 'csc-product-description-read-more' as const

  constructor(target: string) {
    super(CscProductDescriptionReadMoreEvent.type, { detail: { target }, cancelable: true, bubbles: true, composed: true })
  }
}

@customElement('csc-product-description')
export class CSCProductDescription extends HeadingSlotMixin(LitElement) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @state()
  private productDescriptions: ProductDescriptions | null = null

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (product) this.productDescriptions = product
    })
  }

  @property({ type: String })
  description = ''

  @property({ type: String, attribute: 'data-source' })
  dataSource: ProductDescriptionDataSource = 'description'

  @property({ type: Boolean, attribute: 'use-expandable' })
  useExpandable = false

  @property({ type: String, attribute: 'read-more-target' })
  readMoreTarget = ''

  private get activeDescription() {
    if (this.description) return this.description
    const descriptions = this.productDescriptions
    return descriptions?.[this.dataSource] || descriptions?.description || ''
  }

  private handleReadMore() {
    if (!this.dispatchEvent(new CscProductDescriptionReadMoreEvent(this.readMoreTarget))) return
    const target = document.getElementById(this.readMoreTarget)
    if (!target) {
      console.warn(`<csc-product-description> "read-more-target" element "#${this.readMoreTarget}" was not found.`)
      return
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  private renderContent() {
    const classes = classMap({
      'description': true,
      'description--preview': !!this.readMoreTarget,
    })
    const paragraph = html`
      <cse-text-group>
        <div slot="heading">${this.renderHeadingSlot()}</div>
        <div class=${classes}>${unsafeHTML(this.activeDescription)}</div>
      </cse-text-group>
    `

    if (!this.useExpandable || this.readMoreTarget) return paragraph

    return html`
      <cse-expandable>
        <cse-link slot="toggle-open">${t('show_more', { ns: 'csc-product-description', default: 'Show more' })}</cse-link>
        <cse-link slot="toggle-close">${t('show_less', { ns: 'csc-product-description', default: 'Show less' })}</cse-link>
        ${paragraph}
      </cse-expandable>
    `
  }

  override render() {
    if(!this.activeDescription) return nothing
    if (!this.readMoreTarget) return this.renderContent()

    return html`
      ${this.renderContent()}
      <cse-link class="read-more" @click=${this.handleReadMore}>
        ${t('read_more', { ns: 'csc-product-description', default: 'Read more' })}
      </cse-link>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-product-description': CSCProductDescription
  }
}
